import { Check, Info } from '@music-vine/cadence/icons';
import { useEffect, useMemo, useState } from 'react';
import useTWScreens from '@/hooks/ui/useTWScreens';
import Tooltip from '@/components/Tooltip';
import { cn } from '@/lib/utils';
import { Badge, StackingCard, StackingCardContent, StackingCardDescription, StackingCardHeader, StackingCardList, StackingCardListItem, StackingCardTitle } from '@music-vine/cadence/ui';
import { CardPlanSubtitle, type CardPlanSubtitleProps } from './CardPlanSubtitle';
import type { PlanBenefit } from '@/components/Modals/SignUp/constants/plans';
import type { TooltipProps } from '@/components/Tooltip';
import type { Plan } from '@/fixtures/plans';
import { LIBRARY_FREE_PERCENTAGE, LIBRARY_FREE_PERCENTAGE_TOKEN } from '@/constants/library-free-percentage';
export const Feature = ({
  children,
  tooltip,
  tooltipSide = 'bottom',
  tooltipAlign = 'end',
  variant = 'default'
}: {
  children: React.ReactNode;
  tooltip?: string;
  tooltipSide?: TooltipProps['side'];
  tooltipAlign?: TooltipProps['align'];
  variant?: 'default' | 'uppbeat';
}) => {
  const bulletIcon = <Check className={cn('size-[14px] self-center', {
    'text-pink-500': variant === 'uppbeat',
    'text-gray-950 dark:text-white': variant === 'default'
  })} />;
  return <StackingCardListItem className="font-normal font-sans text-gray-700 text-sm dark:text-gray-400" customBullet={bulletIcon} data-sentry-element="StackingCardListItem" data-sentry-component="Feature" data-sentry-source-file="CardPlan.tsx">
      <div className="inline-block">
        {children}
        {tooltip ? <Tooltip align={tooltipAlign} className="z-[9999]" label={tooltip} showOnClick side={tooltipSide}>
            <Info className="mb-1 ml-1 size-3" />
          </Tooltip> : null}
      </div>
    </StackingCardListItem>;
};
export interface CardPlanProps {
  /** Label of the highlight badge next to the plan name. */
  badgeLabel?: string;
  benefits: PlanBenefit[] | string | React.ReactNode;
  collapsible?: boolean;
  description?: string;
  hideCheckbox?: boolean;
  id: Plan['name'];
  // Legacy props for backward compatibility (optional)
  isChecked?: boolean;
  isPopular?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Content rendered inside the expanded card, above the tick list. */
  preList?: React.ReactNode;
  selectable?: boolean;
  subtitle?: CardPlanSubtitleProps;
  title: Plan['name'];
  value?: string;
}
export const CardPlan = ({
  id,
  title,
  description,
  subtitle,
  badgeLabel,
  benefits,
  preList,
  isPopular = false,
  collapsible = true,
  value,
  selectable,
  // Legacy props (for backward compatibility)
  isChecked,
  onCheckedChange,
  hideCheckbox = false
}: CardPlanProps) => {
  const [initiallyCollapsed, setInitiallyCollapsed] = useState<boolean | null>(null);
  const {
    isMd
  } = useTWScreens();

  // On mobile devices (below md breakpoint), start collapsed
  // On desktop devices (md and above), start expanded
  useEffect(() => {
    if (typeof isMd === 'boolean') {
      setInitiallyCollapsed(!isMd);
    }
  }, [isMd]);
  const benefitItems = useMemo(() => {
    if (typeof benefits === 'string') {
      return benefits.split('\n').filter(Boolean).map((line, index) => ({
        id: index + 1,
        description: line.replace('- ', '').replaceAll(LIBRARY_FREE_PERCENTAGE_TOKEN, LIBRARY_FREE_PERCENTAGE).trim(),
        tooltip: undefined
      }));
    }
    if (Array.isArray(benefits)) {
      // Handle PlanBenefit[] structure
      if (benefits.length > 0 && 'text' in benefits[0]) {
        return benefits.map((benefit: PlanBenefit) => ({
          id: benefit.id,
          description: benefit.text,
          tooltip: benefit.tooltip
        }));
      }
      return benefits;
    }

    // If benefits is a ReactNode, wrap it in an array with a single feature
    return [{
      id: 1,
      description: benefits,
      tooltip: undefined
    }];
  }, [benefits]);
  const showFeaturedMark = isPopular && collapsible;
  if (initiallyCollapsed === null) {
    return null;
  }
  return <StackingCard className="min-w-64 max-w-96" initiallyCollapsed={initiallyCollapsed} onSelectedChange={onCheckedChange} selectable={selectable ?? !hideCheckbox} selected={isChecked} value={value} data-sentry-element="StackingCard" data-sentry-component="CardPlan" data-sentry-source-file="CardPlan.tsx">
      <StackingCardHeader data-sentry-element="StackingCardHeader" data-sentry-source-file="CardPlan.tsx">
        <StackingCardTitle data-sentry-element="StackingCardTitle" data-sentry-source-file="CardPlan.tsx">{title}</StackingCardTitle>
        {!!showFeaturedMark && <Badge className="xxs:block hidden font-semibold uppercase" size="xs" variant="brand">
            {badgeLabel ?? 'Popular'}
          </Badge>}
        <CardPlanSubtitle data={subtitle} data-sentry-element="CardPlanSubtitle" data-sentry-source-file="CardPlan.tsx" />
      </StackingCardHeader>

      <StackingCardContent collapsible={collapsible} description={description ? <StackingCardDescription>{description}</StackingCardDescription> : undefined} minimal data-sentry-element="StackingCardContent" data-sentry-source-file="CardPlan.tsx">
        {preList}
        <StackingCardList data-sentry-element="StackingCardList" data-sentry-source-file="CardPlan.tsx">
          {benefitItems.map(item => <Feature key={item.id} tooltip={item.tooltip} tooltipSide={collapsible ? 'top' : 'bottom'}>
              {'description' in item ? item.description : item.text}
            </Feature>)}
        </StackingCardList>
      </StackingCardContent>
    </StackingCard>;
};